import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
    CdkConnectedOverlay,
    type ConnectedPosition,
    Overlay,
    OverlayModule,
    type ScrollStrategy,
} from '@angular/cdk/overlay';
import { DomPortal } from '@angular/cdk/portal';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    effect,
    inject,
    Injector,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, type MatDialogRef } from '@angular/material/dialog';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import { forkJoin, take } from 'rxjs';
import { QpxButton, QpxIcon, QpxTooltip } from '../../../directives';
import { DEFAULT_POPOVER_VIEWPORT_MARGIN } from '../../../services/popover/inject-popover';
import { DialogService } from '../../dialog/dialog.service';
import type { DialogResult } from '../../dialog/models';
import type { FilterWorkspaceController } from '../controller';
import { QpxFilterWorkspaceSurface } from './filter-workspace-surface';

const FILTER_WORKSPACE_POSITIONS: ConnectedPosition[] = [
    {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8,
    },
    {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 8,
    },
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8,
    },
];

let nextWorkspaceId = 0;

@Component({
    selector: 'qpx-filter-workspace',
    imports: [
        CdkTrapFocus,
        MatBadgeModule,
        MatButtonModule,
        NgIcon,
        OverlayModule,
        QpxButton,
        QpxIcon,
        QpxTooltip,
        QpxFilterWorkspaceSurface,
    ],
    templateUrl: './filter-workspace.html',
    styleUrl: './filter-workspace.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'qpx-filter-workspace',
    },
})
export class QpxFilterWorkspace {
    //#region Dependency injection
    private readonly overlay = inject(Overlay);
    private readonly viewport = injectViewportBreakpoints();
    private readonly dialogService = inject(DialogService);
    private readonly matDialog = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    //#endregion

    //#region Inputs
    public readonly controller = input.required<FilterWorkspaceController>();
    public readonly disabled = input(false);
    public readonly disabledReason = input<string | null>(null);
    public readonly dialogLabel = input('Filter results');
    public readonly hiddenDimensionKeys = input<readonly string[]>([]);
    public readonly iconOnly = input(false);
    public readonly appearance = input<'elevated' | 'outlined'>('elevated');
    public readonly mobileBreakpoint = input<'tablet' | 'tablet768'>(
        'tablet768'
    );
    public readonly triggerLabel = input('Filters');
    //#endregion

    //#region Template references
    private readonly triggerRef = viewChild<
        unknown,
        ElementRef<HTMLButtonElement>
    >('triggerButton', {
        read: ElementRef,
    });
    private readonly connectedOverlay = viewChild(CdkConnectedOverlay);
    private readonly contentHost =
        viewChild.required<ElementRef<HTMLElement>>('workspaceContent');
    //#endregion

    //#region State
    protected readonly isOpen = signal(false);
    private readonly isPhone = this.viewport.isPhone;
    private readonly isBelowTablet768 = this.viewport.lessThan('tablet768');
    protected readonly isMobile = computed(() =>
        this.mobileBreakpoint() === 'tablet'
            ? this.isPhone()
            : this.isBelowTablet768()
    );
    private readonly isCompactTrigger = this.viewport.isPhone;
    protected readonly showIconOnly = computed(
        () => this.iconOnly() || this.isCompactTrigger()
    );
    protected readonly positions = FILTER_WORKSPACE_POSITIONS;
    protected readonly popoverViewportMargin = DEFAULT_POPOVER_VIEWPORT_MARGIN;
    protected readonly scrollStrategy: ScrollStrategy =
        this.overlay.scrollStrategies.block();
    protected readonly surfaceId = `qpx-filter-workspace-${nextWorkspaceId++}`;
    protected readonly contentPortal = computed(
        () => new DomPortal(this.contentHost())
    );
    protected readonly disabledReasonId = `${this.surfaceId}-disabled-reason`;
    protected readonly hasVisibleActiveFilters = computed(() => {
        const hiddenKeys = new Set(this.hiddenDimensionKeys());

        return this.controller()
            .appliedActiveKeys()
            .some((key) => !hiddenKeys.has(key));
    });
    protected readonly triggerAriaLabel = computed(() => {
        return this.hasVisibleActiveFilters()
            ? `${this.triggerLabel()}, active filters`
            : this.triggerLabel();
    });
    protected readonly triggerTooltip = computed(() => {
        if (this.disabled() && this.disabledReason()) {
            return this.disabledReason();
        }

        return this.triggerLabel();
    });
    private mobileDialogRef:
        | MatDialogRef<QpxFilterWorkspaceSurface, DialogResult>
        | undefined;
    private visibleSummarySignature: string | undefined;
    private hiddenDimensionKeySignature: string | undefined;
    private dialogsBeforeOpen: ReadonlySet<object> = new Set();
    //#endregion

    constructor() {
        effect(() => {
            if (this.isMobile() && this.mobileDialogRef === undefined) return;

            if (!this.isMobile() && this.mobileDialogRef) {
                this.controller().cancel();
                this.isOpen.set(false);
                this.mobileDialogRef.close(false);
            }
        });
        effect(() => {
            const nextSignature = this.hiddenDimensionKeys().join('\u0000');
            const dimensionsChanged =
                this.hiddenDimensionKeySignature !== undefined &&
                this.hiddenDimensionKeySignature !== nextSignature;

            this.hiddenDimensionKeySignature = nextSignature;

            if (dimensionsChanged && this.isOpen()) {
                if (this.mobileDialogRef) {
                    this.controller().cancel();
                    this.isOpen.set(false);
                    this.mobileDialogRef.close(false);
                } else {
                    this.dismissPopover();
                }
            }
        });
        effect(() => {
            const popoverOpen = this.isOpen() && !this.isMobile();
            const hiddenKeys = new Set(this.hiddenDimensionKeys());
            const nextSignature = popoverOpen
                ? this.controller()
                      .summaries()
                      .filter((summary) => !hiddenKeys.has(summary.key))
                      .map(
                          (summary) =>
                              `${summary.key}\u0000${summary.label}\u0000${summary.value}`
                      )
                      .join('\u0001')
                : undefined;
            const summariesChanged =
                this.visibleSummarySignature !== undefined &&
                this.visibleSummarySignature !== nextSignature;

            this.visibleSummarySignature = nextSignature;

            if (!popoverOpen || !summariesChanged) return;

            afterNextRender(
                () => {
                    if (!this.isOpen() || this.isMobile()) return;

                    this.connectedOverlay()?.overlayRef.updatePosition();
                },
                { injector: this.injector }
            );
        });
    }

    //#region Template methods
    protected toggle(): void {
        if (this.disabled() || this.mobileDialogRef) return;

        if (this.isOpen()) {
            this.dismissPopover();
        } else {
            this.dialogsBeforeOpen = new Set(this.matDialog.openDialogs);
            this.controller().begin();

            if (this.isMobile()) {
                this.openMobileDialog();
            } else {
                this.isOpen.set(true);
            }
        }
    }

    protected dismissPopover(): void {
        this.closePopover(true);
    }

    protected applyPopover(): void {
        this.closePopover(false);
    }

    protected onOverlayKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        event.stopPropagation();
        this.dismissPopover();
    }

    protected onDetach(): void {
        if (this.isOpen()) this.dismissPopover();
    }
    //#endregion

    //#region Private methods
    private closePopover(cancelDraft: boolean): void {
        if (!this.isOpen()) return;

        if (cancelDraft) this.controller().cancel();
        this.isOpen.set(false);
        this.restoreTriggerFocus();
    }

    private restoreTriggerFocus(
        onlyIfOrphaned = false,
        dialogsBeforeOpen = this.dialogsBeforeOpen,
        closingDialogIds: ReadonlySet<string> = new Set()
    ): void {
        setTimeout(() => {
            if (this.destroyRef.destroyed || this.isOpen()) return;

            const newDialogs = this.matDialog.openDialogs.filter(
                (dialog) => !dialogsBeforeOpen.has(dialog)
            );
            if (newDialogs.length) {
                const introducedDialogIds = new Set([
                    ...closingDialogIds,
                    ...newDialogs.map((dialog) => dialog.id),
                ]);
                forkJoin(
                    newDialogs.map((dialog) =>
                        dialog.afterClosed().pipe(take(1))
                    )
                )
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() =>
                        this.restoreTriggerFocus(
                            true,
                            dialogsBeforeOpen,
                            introducedDialogIds
                        )
                    );
                return;
            }

            const activeElement = document.activeElement;
            if (
                onlyIfOrphaned &&
                activeElement !== null &&
                activeElement !== document.body &&
                activeElement.isConnected &&
                !closingDialogIds.has(
                    activeElement.closest('mat-dialog-container')?.id ?? ''
                )
            )
                return;

            this.triggerRef()?.nativeElement?.focus();
        });
    }

    private openMobileDialog(): void {
        this.isOpen.set(true);
        const dialogRef = this.dialogService.openDialog(
            {
                component: QpxFilterWorkspaceSurface,
                componentInputs: {
                    appearance: 'dialog',
                    content: this.contentPortal(),
                    controller: this.controller(),
                    dialogLabel: this.dialogLabel(),
                    hiddenDimensionKeys: this.hiddenDimensionKeys(),
                    surfaceId: this.surfaceId,
                    triggerLabel: this.triggerLabel(),
                },
            },
            {
                autoFocus: 'first-heading',
                ariaLabel: this.dialogLabel(),
                ariaModal: true,
                disableClose: false,
                fullscreenBelow: this.mobileBreakpoint(),
                panelClass: ['filter-workspace-dialog', 'no-padding'],
                restoreFocus: false,
                size: 'md',
            }
        );

        this.mobileDialogRef = dialogRef;
        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result !== true) this.controller().cancel();
                this.mobileDialogRef = undefined;
                this.isOpen.set(false);
                this.restoreTriggerFocus();
            });
    }
    //#endregion
}
